import { faQuestionCircle, faTicket } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import {
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
} from 'react-intl';
import styled from 'styled-components';

import { Impact } from '@sorare/core/src/atoms/animations/Impact';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  HeadlineL,
  HeadlineS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_MARKET_PRIMARY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { Auction } from 'components/transfers/Auction';
import { InstantBuy } from 'components/transfers/InstantBuy';

const messages = defineMessages({
  howToWin: {
    id: 'marketplace.wheel.tickets.howToWin',
    defaultMessage: 'How to win tickets?',
  },
});
const Content = styled(Vertical).attrs({
  gap: 5,
})`
  padding: var(--triple-unit);
`;

const Bullet = styled(Horizontal).attrs({
  gap: 2,
})`
  > :first-child {
    flex-shrink: 0;
  }
`;

export const Tickets = ({ nb = 0 }: { nb: number }) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();

  return (
    <>
      <Vertical center>
        <HeadlineS>
          <FormattedMessage
            id="marketplace.wheel.tickets.title"
            defaultMessage="Tickets"
          />
        </HeadlineS>
        <Impact trigger={nb}>
          <HeadlineL>
            <Horizontal>
              <FontAwesomeIcon icon={faTicket} />
              <FormattedNumber value={nb} />
            </Horizontal>
          </HeadlineL>
        </Impact>
        <Button color="transparent" size="small" onClick={() => setOpen(true)}>
          <LabelS color="var(--c-nd-500)">
            <Horizontal>
              <FontAwesomeIcon icon={faQuestionCircle} />
              <FormattedMessage {...messages.howToWin} />
            </Horizontal>
          </LabelS>
        </Button>
      </Vertical>
      <Dialog
        open={open}
        maxWidth="xs"
        onClose={() => setOpen(false)}
        title={formatMessage(messages.howToWin)}
      >
        <Content>
          <Vertical gap={2}>
            <Bullet>
              <InstantBuy size={4} color="var(--c-white)" />
              <BodyM color="var(--c-nd-600)" as="p">
                <FormattedMessage
                  id="marketplace.wheel.tickets.howToWin.instantBuy"
                  defaultMessage="Buy a 26 season Card through Instant Buy"
                />
              </BodyM>
            </Bullet>
            <Bullet>
              <Auction size={4} color="var(--c-white)" />
              <BodyM color="var(--c-nd-600)" as="p">
                <FormattedMessage
                  id="marketplace.wheel.tickets.howToWin.auction"
                  defaultMessage="Buy a 26 season Card through Auction"
                />
              </BodyM>
            </Bullet>
            <Bullet>
              <Auction size={4} color="var(--c-white)" />
              <BodyM color="var(--c-nd-600)" as="p">
                <FormattedMessage
                  id="marketplace.wheel.tickets.howToWin.bidToEarn"
                  defaultMessage="End a 26 Season Card Auction with the 2nd, 3rd, or 4th highest bid"
                />
              </BodyM>
            </Bullet>
          </Vertical>
          <BodyM color="var(--c-nd-600)" as="p">
            <FormattedMessage
              id="marketplace.wheel.tickets.howToWin.other"
              defaultMessage="Other ways of winning tickets may be added in the future — stay posted"
            />
          </BodyM>
          <Button
            size="large"
            fullWidth
            color="purple"
            to={generateSportPath(ANY_SPORT_MARKET_PRIMARY, { sport })}
          >
            <FormattedMessage
              id="marketplace.wheel.tickets.shopCards"
              defaultMessage="Shop Cards"
            />
          </Button>
        </Content>
      </Dialog>
    </>
  );
};
